import { useState, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";

type Kind = "forum" | "thread" | "user";

interface Props {
  kind: Kind;
  id: number | string;
  label?: string;
  /**
   * Query keys to invalidate while we poll for fresh data. They will be
   * refetched every 2 seconds until either:
   *   - one of the watched values changes (success),
   *   - we hit the timeout (15s)
   */
  invalidate?: ReadonlyArray<readonly unknown[]>;
  /**
   * Optional : a comparator that returns true when the fresh data is "new
   * enough". Receives the current cached values for each invalidated key.
   * Default : any non-null cached data is considered fresh after the first
   * refetch.
   */
  isFresh?: (snapshots: unknown[]) => boolean;
  className?: string;
  variant?: "default" | "outline" | "ghost" | "secondary";
  size?: "default" | "sm" | "icon";
}

const REFRESHERS = {
  forum: api.refreshForum,
  thread: api.refreshThread,
  user: api.refreshUser,
} as const;

const POLL_INTERVAL = 2000;
const POLL_MAX_TRIES = 8; // ~16s

export function RefreshButton({
  kind,
  id,
  label,
  invalidate = [],
  isFresh,
  className,
  variant = "outline",
  size = "sm",
}: Props) {
  const qc = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [pending, setPending] = useState(false);
  const [tries, setTries] = useState(0);
  const cancelled = useRef(false);

  useEffect(() => () => {
    cancelled.current = true;
  }, []);

  async function pollUntilFresh() {
    // Snapshot of the BEFORE values so we can detect changes.
    const before = invalidate.map((k) => qc.getQueryData([...k]));
    let attempt = 0;
    cancelled.current = false;
    setPending(true);
    setTries(0);

    const tick = async () => {
      attempt++;
      setTries(attempt);
      if (cancelled.current) return;
      // Force a refetch of each watched query.
      await Promise.all(
        invalidate.map((k) =>
          qc.invalidateQueries({ queryKey: [...k], refetchType: "active" })
        )
      );
      // Wait a microtask so the freshly-fetched data lands in the cache.
      await new Promise((r) => setTimeout(r, 100));
      const after = invalidate.map((k) => qc.getQueryData([...k]));
      const changed =
        isFresh != null
          ? isFresh(after)
          : after.some((v, i) => JSON.stringify(v) !== JSON.stringify(before[i]));
      if (changed) {
        setPending(false);
        toast.success(`${label ?? "Données mises à jour"}`);
        return;
      }
      if (attempt >= POLL_MAX_TRIES) {
        setPending(false);
        // The job is queued server-side (Redis) and the scraper picks it up
        // on its own schedule — a full re-scrape can take well over our poll
        // window. So this is NOT a failure: the request was accepted, the
        // fresh data will simply land in the background.
        toast.info(
          `${label ?? "Rafraîchissement"} lancé en arrière-plan — les nouvelles données apparaîtront d'ici quelques minutes`
        );
        return;
      }
      setTimeout(tick, POLL_INTERVAL);
    };
    setTimeout(tick, POLL_INTERVAL);
  }

  const m = useMutation({
    mutationFn: () => REFRESHERS[kind](id),
    onSuccess: () => {
      toast.info(`${label ?? "Rafraîchissement"} demandé…`);
      pollUntilFresh();
    },
    onError: (e) => {
      toast.error(`Refresh impossible : ${e instanceof Error ? e.message : "?"}`);
    },
  });

  // The refresh endpoints are auth-gated server-side (RequireUser). Don't
  // expose the button to anonymous visitors — it would 401.
  if (!isAuthenticated) return null;

  const busy = m.isPending || pending;
  // Keep the visible label width stable: the attempt counter would otherwise
  // grow the button and squeeze the page title. It lives in the tooltip.
  const title =
    pending && tries > 0
      ? `Re-scraper ce ${kind} — vérification ${tries}/${POLL_MAX_TRIES}`
      : `Re-scraper ce ${kind}`;

  return (
    <Button
      size={size}
      variant={variant}
      onClick={() => m.mutate()}
      disabled={busy}
      className={cn(className)}
      title={title}
    >
      {busy ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <RefreshCw className="size-3.5" />
      )}
      {size !== "icon" && (busy ? "Rafraîchissement…" : label ?? "Rafraîchir")}
    </Button>
  );
}
